import { type DefaultContext, mutationPromise } from '@egw/clients'
import {
  type StartWebAuthnAuthenticationInput,
  type StartWebAuthnAuthenticationSuccess,
  type StartWebAuthnAuthenticationError,
  type AuthenticateWebAuthnInput,
  type AuthenticateWebAuthnSuccess,
  type AuthenticateWebAuthnError,
  type PingSessionOutput,
  type SignOutOutput,
  type RegisterWebAuthnInput,
  type RegisterWebAuthnOutput,
  type StartWebAuthnRegistrationInput,
  type StartWebAuthnRegistrationOutput,
  type AccountRecoveryInput,
  type AccountRecoveryOutput,
  type AccountRecoveryInputV2,
  type AccountRecoveryOutputV2,
  type LoginOtpVerificationInput,
  type LoginOtpVerificationOutput,
  type RefreshTokenResponse,
  type RemovePasskeyInput,
  type RemovePasskeyOutput,
  type AuthSignOutInput,
  type AuthSignOutResponse,
  type AuthsvsPasskeysOutput,
} from '../../models'
import { createMutationHook, createQueryHook } from '../../hooks'
import { refreshTokenQuery } from './queries'

/**
 * Start passkey authentication
 */
export const startWebAuthnAuthentication = async (
  startWebAuthnAuthenticationInput: StartWebAuthnAuthenticationInput,
  context?: DefaultContext,
): Promise<StartWebAuthnAuthenticationSuccess | StartWebAuthnAuthenticationError> => {
  const response = (await mutationPromise(
    {
      name: 'startWebAuthnAuthentication',
      query: `
        mutation startWebAuthnAuthentication($startWebAuthnAuthenticationInput: StartWebAuthnAuthenticationInput!) {
          startWebAuthnAuthentication(startWebAuthnAuthenticationInput: $startWebAuthnAuthenticationInput) {
            ... on StartWebAuthnAuthenticationSuccess {
              __typename
              publicKeyCredentialRequestOptions
            }
            ... on StartWebAuthnAuthenticationError {
              __typename
              errorType
            }
          }
        }`,
    },
    {
      startWebAuthnAuthenticationInput,
    },
    context,
  )) as { startWebAuthnAuthentication: StartWebAuthnAuthenticationSuccess | StartWebAuthnAuthenticationError }
  return response.startWebAuthnAuthentication
}

export const useStartWebAuthnAuthentication = createMutationHook<
  StartWebAuthnAuthenticationInput,
  StartWebAuthnAuthenticationSuccess | StartWebAuthnAuthenticationError
>(startWebAuthnAuthentication)

/**
 * Start passkey authentication (authsvs version)
 */
export const authsvsStartWebAuthnAuthentication = async (
  startWebAuthnAuthenticationInput: StartWebAuthnAuthenticationInput,
  context?: DefaultContext,
): Promise<StartWebAuthnAuthenticationSuccess | StartWebAuthnAuthenticationError> => {
  const response = (await mutationPromise(
    {
      name: 'authsvsStartWebAuthnAuthentication',
      query: `
        mutation authsvsStartWebAuthnAuthentication($startWebAuthnAuthenticationInput: StartWebAuthnAuthenticationInput!) {
          authsvsStartWebAuthnAuthentication(startWebAuthnAuthenticationInput: $startWebAuthnAuthenticationInput) {
            ... on StartWebAuthnAuthenticationSuccess {
              __typename
              publicKeyCredentialRequestOptions
            }
            ... on StartWebAuthnAuthenticationError {
              __typename
              errorType
              intelligenceResult {
                userMessage
                suggestedActions
                fallbackOptions
              }
            }
          }
        }`,
    },
    {
      startWebAuthnAuthenticationInput,
    },
    context,
  )) as { authsvsStartWebAuthnAuthentication: StartWebAuthnAuthenticationSuccess | StartWebAuthnAuthenticationError }
  return response.authsvsStartWebAuthnAuthentication
}

export const useAuthsvsStartWebAuthnAuthentication = createMutationHook<
  StartWebAuthnAuthenticationInput,
  StartWebAuthnAuthenticationSuccess | StartWebAuthnAuthenticationError
>(authsvsStartWebAuthnAuthentication)

/**
 * Authenticate passkey
 */
export const authenticateWebAuthn = async (
  authenticateWebAuthnInput: AuthenticateWebAuthnInput,
  context?: DefaultContext,
): Promise<AuthenticateWebAuthnSuccess | AuthenticateWebAuthnError> => {
  const response = (await mutationPromise(
    {
      name: 'authenticateWebAuthn',
      query: `
        mutation authenticateWebAuthn($authenticateWebAuthnInput: AuthenticateWebAuthnInput!) {
          authenticateWebAuthn(authenticateWebAuthnInput: $authenticateWebAuthnInput) {
            ... on AuthenticateWebAuthnSuccess {
              __typename
              customerId
              isManualReviewApplicant
            }
            ... on AuthenticateWebAuthnError {
              __typename
              errorType
            }
          }
        }`,
    },
    {
      authenticateWebAuthnInput,
    },
    context,
  )) as { authenticateWebAuthn: AuthenticateWebAuthnSuccess | AuthenticateWebAuthnError }
  return response.authenticateWebAuthn
}

export const useAuthenticateWebAuthn = createMutationHook<
  AuthenticateWebAuthnInput,
  AuthenticateWebAuthnSuccess | AuthenticateWebAuthnError
>(authenticateWebAuthn)

/**
 * Authenticate passkey (authsvs version)
 */
export const authsvsAuthenticateWebAuthn = async (
  authenticateWebAuthnInput: AuthenticateWebAuthnInput,
  context?: DefaultContext,
): Promise<AuthenticateWebAuthnSuccess | AuthenticateWebAuthnError> => {
  const response = (await mutationPromise(
    {
      name: 'authsvsAuthenticateWebAuthn',
      query: `
        mutation authsvsAuthenticateWebAuthn($authenticateWebAuthnInput: AuthenticateWebAuthnInput!) {
          authsvsAuthenticateWebAuthn(authenticateWebAuthnInput: $authenticateWebAuthnInput) {
            ... on AuthenticateWebAuthnSuccess {
              __typename
              userId
            }
            ... on AuthenticateWebAuthnError {
              __typename
              errorType
            }
          }
        }`,
    },
    {
      authenticateWebAuthnInput,
    },
    context,
  )) as { authsvsAuthenticateWebAuthn: AuthenticateWebAuthnSuccess | AuthenticateWebAuthnError }
  return response.authsvsAuthenticateWebAuthn
}

export const useAuthsvsAuthenticateWebAuthn = createMutationHook<
  AuthenticateWebAuthnInput,
  AuthenticateWebAuthnSuccess | AuthenticateWebAuthnError
>(authsvsAuthenticateWebAuthn)

/**
 * Start passkey registration
 */
export const startWebAuthnRegistration = async (
  startWebAuthnRegistrationInput: StartWebAuthnRegistrationInput,
  context?: DefaultContext,
): Promise<StartWebAuthnRegistrationOutput> => {
  const response = (await mutationPromise(
    {
      name: 'startWebAuthnRegistration',
      query: `
        mutation startWebAuthnRegistration($startWebAuthnRegistrationInput: StartWebAuthnRegistrationInput!) {
          startWebAuthnRegistration(startWebAuthnRegistrationInput: $startWebAuthnRegistrationInput) {
            ... on StartWebAuthnRegistrationSuccess {
              __typename
              publicKeyCredentialCreationOptions
            }
            ... on StartWebAuthnRegistrationError {
              __typename
              errorType
            }
          }
        }`,
    },
    {
      startWebAuthnRegistrationInput,
    },
    context,
  )) as { startWebAuthnRegistration: StartWebAuthnRegistrationOutput }
  return response.startWebAuthnRegistration
}

export const useStartWebAuthnRegistration = createMutationHook<
  StartWebAuthnRegistrationInput,
  StartWebAuthnRegistrationOutput
>(startWebAuthnRegistration)

/**
 * Start passkey registration (authsvs version)
 */
export const authsvsStartWebAuthnRegistration = async (
  startWebAuthnRegistrationInput: StartWebAuthnRegistrationInput,
  context?: DefaultContext,
): Promise<StartWebAuthnRegistrationOutput> => {
  const response = (await mutationPromise(
    {
      name: 'authsvsStartWebAuthnRegistration',
      query: `
        mutation authsvsStartWebAuthnRegistration($startWebAuthnRegistrationInput: StartWebAuthnRegistrationInput!) {
          authsvsStartWebAuthnRegistration(startWebAuthnRegistrationInput: $startWebAuthnRegistrationInput) {
            ... on StartWebAuthnRegistrationSuccess {
              __typename
              publicKeyCredentialCreationOptions
            }
            ... on StartWebAuthnRegistrationError {
              __typename
              errorType
            }
          }
        }`,
    },
    {
      startWebAuthnRegistrationInput,
    },
    context,
  )) as { authsvsStartWebAuthnRegistration: StartWebAuthnRegistrationOutput }
  return response.authsvsStartWebAuthnRegistration
}

export const useAuthsvsStartWebAuthnRegistration = createMutationHook<
  StartWebAuthnRegistrationInput,
  StartWebAuthnRegistrationOutput
>(authsvsStartWebAuthnRegistration)

/**
 * Create passkey
 */
export const registerWebAuthn = async (
  registerWebAuthnInput: RegisterWebAuthnInput,
  context?: DefaultContext,
): Promise<RegisterWebAuthnOutput> => {
  const response = (await mutationPromise(
    {
      name: 'registerWebAuthn',
      query: `
        mutation registerWebAuthn($registerWebAuthnInput: RegisterWebAuthnInput!) {
          registerWebAuthn(registerWebAuthnInput: $registerWebAuthnInput) {
            ... on RegisterWebAuthnSuccess {
              __typename
              userId
            }
            ... on RegisterWebAuthnError {
              __typename
              errorType
            }
          }
        }`,
    },
    {
      registerWebAuthnInput,
    },
    context,
  )) as { registerWebAuthn: RegisterWebAuthnOutput }
  return response.registerWebAuthn
}

export const useRegisterWebAuthn = createMutationHook<RegisterWebAuthnInput, RegisterWebAuthnOutput>(registerWebAuthn)

/**
 * Create passkey (authsvs version)
 */
export const authsvsRegisterWebAuthn = async (
  registerWebAuthnInput: RegisterWebAuthnInput,
  context?: DefaultContext,
): Promise<RegisterWebAuthnOutput> => {
  const response = (await mutationPromise(
    {
      name: 'authsvsRegisterWebAuthn',
      query: `
        mutation authsvsRegisterWebAuthn($registerWebAuthnInput: RegisterWebAuthnInput!) {
          authsvsRegisterWebAuthn(registerWebAuthnInput: $registerWebAuthnInput) {
            ... on RegisterWebAuthnSuccess {
              __typename
              userId
            }
            ... on RegisterWebAuthnError {
              __typename
              errorType
            }
          }
        }`,
    },
    {
      registerWebAuthnInput,
    },
    context,
  )) as { authsvsRegisterWebAuthn: RegisterWebAuthnOutput }
  return response.authsvsRegisterWebAuthn
}

export const useAuthsvsRegisterWebAuthn = createMutationHook<RegisterWebAuthnInput, RegisterWebAuthnOutput>(
  authsvsRegisterWebAuthn,
)

/**
 * Sign out (authsvs version)
 */
export const authsvsSignOut = async (
  authSignOutInput?: AuthSignOutInput,
  context?: DefaultContext,
): Promise<AuthSignOutResponse> => {
  const response = (await mutationPromise(
    {
      name: 'authsvsSignOut',
      query: `
        mutation authsvsSignOut($input: AuthSignOutInput) {
          authsvsSignOut(input: $input) {
            success
            message
          }
        }`,
    },
    {
      input: authSignOutInput,
    },
    context,
  )) as { authsvsSignOut: AuthSignOutResponse }
  return response.authsvsSignOut
}

export const useAuthsvsSignOut = createMutationHook<AuthSignOutInput | undefined, AuthSignOutResponse>(authsvsSignOut)

/**
 * Get all passkeys for the authenticated user
 */
export const authsvsGetAllPasskeys = async (context?: DefaultContext): Promise<AuthsvsPasskeysOutput[]> => {
  const response = (await mutationPromise(
    {
      name: 'authsvsGetAllPasskeys',
      query: `
        mutation authsvsGetAllPasskeys {
          authsvsGetAllPasskeys {
            id
            userId
            credentialId
            createdAt
            updatedAt
            aaguid
            devicePlatform
            deviceOs
            deviceOsVersion
            browserName
            browserVersion
            userAgent
            isMobile
            transports
            nickName
          }
        }`,
    },
    {},
    context,
  )) as { authsvsGetAllPasskeys: AuthsvsPasskeysOutput[] }
  return response.authsvsGetAllPasskeys
}

export const useAuthsvsGetAllPasskeys = createQueryHook<undefined, AuthsvsPasskeysOutput[]>(authsvsGetAllPasskeys)

/**
 * Ping user session
 */
export const pingSession = async (context?: DefaultContext): Promise<PingSessionOutput> => {
  const response = (await mutationPromise(
    {
      name: 'pingSession',
      query: `
        mutation pingSession {
          pingSession {
            ... on AuthenticatedSession {
              __typename
              userId
            }
            ... on UnauthenticatedSession {
              __typename
            }
          }
        }`,
    },
    context,
  )) as { pingSession: PingSessionOutput }
  return response.pingSession
}

export const usePingSession = createMutationHook<undefined, PingSessionOutput>(pingSession)

/**
 * Sign out
 */
export const signOut = async (context?: DefaultContext): Promise<SignOutOutput> => {
  const response = (await mutationPromise(
    {
      name: 'signOut',
      query: `
        mutation signOut {
          signOut {
            isSuccessful
          }
        }`,
    },
    context,
  )) as { signOut: SignOutOutput }
  return response.signOut
}

/**
 * Passkey recovery
 */
export const accountRecovery = async (
  accountRecoveryInput: AccountRecoveryInput,
  context?: DefaultContext,
): Promise<AccountRecoveryOutput> => {
  const response = (await mutationPromise(
    {
      name: 'accountRecovery',
      query: `
        mutation accountRecovery($accountRecoveryInput: AccountRecoveryInput!) {
          accountRecovery(accountRecoveryInput: $accountRecoveryInput) {
            ... on AccountRecoveryResponse {
              __typename
              documentVerificationUrl
              publicKeyCredentialCreationOptions
              status
            }
            ... on AccountRecoveryError {
              __typename
              errorType
            }
          }
        }`,
    },
    {
      accountRecoveryInput,
    },
    context,
  )) as { accountRecovery: AccountRecoveryOutput }
  return response.accountRecovery
}

export const useAccountRecovery = createMutationHook<AccountRecoveryInput, AccountRecoveryOutput>(accountRecovery)

/**
 * Passkey recovery
 */
export const accountRecoveryV2 = async (
  accountRecoveryInputV2: AccountRecoveryInputV2,
  context?: DefaultContext,
): Promise<AccountRecoveryOutputV2> => {
  const response = (await mutationPromise(
    {
      name: 'accountRecoveryV2',
      query: `
        mutation accountRecoveryV2($accountRecoveryInputV2: AccountRecoveryInputV2!) {
          accountRecoveryV2(accountRecoveryInputV2: $accountRecoveryInputV2) {
            ... on AccountRecoveryResponseV2 {
              __typename
              documentVerificationToken
              publicKeyCredentialCreationOptions
              status
              verificationId
              documentVerificationBaseUrl
            }
            ... on AccountRecoveryError {
              __typename
              errorType
            }
          }
        }`,
    },
    {
      accountRecoveryInputV2,
    },
    context,
  )) as { accountRecoveryV2: AccountRecoveryOutputV2 }
  return response.accountRecoveryV2
}

export const useAccountRecoveryV2 = createMutationHook<AccountRecoveryInputV2, AccountRecoveryOutputV2>(
  accountRecoveryV2,
)

/**
 * Login OTP verification
 */

export const loginOtpVerification = async (
  loginOtpVerificationInput: LoginOtpVerificationInput,
  context?: DefaultContext,
): Promise<LoginOtpVerificationOutput> => {
  const response = (await mutationPromise(
    {
      name: 'loginOtpVerification',
      query: `
      mutation loginOtpVerification($loginOtpVerificationInput: LoginOtpVerificationInput!) {
        loginOtpVerification(loginOtpVerificationInput: $loginOtpVerificationInput) {
          ... on LoginOtpVerification {
            __typename
            userId
            isManualReviewApplicant        
          }
          ... on ResendOtp {
            __typename
            isSuccessful
          }
          ... on LoginOtpVerificationError {
            __typename
            errorType
          }
        }
      }`,
    },
    {
      loginOtpVerificationInput,
    },
    context,
  )) as { loginOtpVerification: LoginOtpVerificationOutput }
  return response.loginOtpVerification
}

export const useLoginOtpVerifification = createMutationHook<LoginOtpVerificationInput, LoginOtpVerificationOutput>(
  loginOtpVerification,
)

/**
 * Refresh authentication token
 */
export const refreshToken = async (context?: DefaultContext): Promise<RefreshTokenResponse> => {
  const response = (await mutationPromise(
    {
      name: 'authsvsRefreshToken',
      query: refreshTokenQuery,
    },
    {},
    context,
  )) as { authsvsRefreshToken: RefreshTokenResponse }
  return response.authsvsRefreshToken
}

export const useRefreshToken = createMutationHook<undefined, RefreshTokenResponse>(refreshToken)

/**
 * Remove passkey (authsvs version)
 */
export const authsvsRemovePasskey = async (
  removePasskeyInput: RemovePasskeyInput,
  context?: DefaultContext,
): Promise<RemovePasskeyOutput> => {
  const response = (await mutationPromise(
    {
      name: 'authsvsRemovePasskey',
      query: `
        mutation authsvsRemovePasskey($removePasskeyInput: RemovePasskeyInput!) {
          authsvsRemovePasskey(removePasskeyInput: $removePasskeyInput) {
            ... on RemovePasskeySuccess {
              __typename
              success
            }
            ... on RemovePasskeyError {
              __typename
              errorType
            }
          }
        }`,
    },
    {
      removePasskeyInput,
    },
    context,
  )) as { authsvsRemovePasskey: RemovePasskeyOutput }
  return response.authsvsRemovePasskey
}

export const useAuthsvsRemovePasskey = createMutationHook<RemovePasskeyInput, RemovePasskeyOutput>(authsvsRemovePasskey)
